import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOrgs } from '@/contexts/OrgsContext';
import { useGetOrgBilling } from '@/api/auth/billing/billing';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { CustomerPortalButton } from '@/components/billing/CustomerPortalButton';
import { CreditCard, MapPin } from 'lucide-react';

interface OrgBillingCardProps {
  hasStripeCustomer: boolean;
}

/**
 * Displays organization billing information including address and payment method.
 * Only fetches data when the organization has a Stripe customer account.
 */
export function OrgBillingCard({ hasStripeCustomer }: OrgBillingCardProps) {
  const { t } = useLanguage();
  const { selectedOrganizationId } = useOrgs();

  const {
    data: billingResponse,
    isLoading,
    error,
    refetch,
  } = useGetOrgBilling(selectedOrganizationId ?? '', {
    query: {
      enabled: !!selectedOrganizationId && hasStripeCustomer,
    },
  });

  const billing = billingResponse?.data;

  // Don't render if org doesn't have a Stripe customer
  if (!hasStripeCustomer) {
    return null;
  }

  /**
   * Formats the billing address into a display string
   */
  const formatAddress = () => {
    if (!billing?.address) return null;

    const { line1, line2, city, state, postalCode, country } = billing.address;
    const parts: string[] = [];

    if (line1) parts.push(line1);
    if (line2) parts.push(line2);

    const cityStateZip = [city, state, postalCode].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);

    if (country) parts.push(country);

    return parts;
  };

  /**
   * Gets the translated payment method type name
   */
  const getPaymentMethodTypeName = (type: string | undefined): string => {
    if (!type) return t.billing.orgBilling.paymentMethodTypes.unknown;

    const typeKey = type as keyof typeof t.billing.orgBilling.paymentMethodTypes;
    return t.billing.orgBilling.paymentMethodTypes[typeKey];
  };

  /**
   * Formats the payment method for display based on type
   */
  const formatPaymentMethod = () => {
    if (!billing?.defaultPaymentMethod) return null;

    const { type, cardBrand, cardLast4, cardExpMonth, cardExpYear } = billing.defaultPaymentMethod;
    const typeName = getPaymentMethodTypeName(type);

    // For card-based payment methods, show brand, last 4, and expiry
    if (type === 'card' || type === 'card_present') {
      if (!cardLast4) return { typeName, details: null, expiry: null };

      const brand = cardBrand ? cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1) : typeName;
      const expiry =
        cardExpMonth && cardExpYear
          ? `${String(cardExpMonth).padStart(2, '0')}/${String(cardExpYear).slice(-2)}`
          : null;

      return { typeName: brand, details: `•••• ${cardLast4}`, expiry };
    }

    // For other payment methods, just show the type name
    return { typeName, details: null, expiry: null };
  };

  /**
   * Renders skeleton loading state
   */
  const renderSkeleton = () => (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Address Section Skeleton */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="space-y-1.5 pl-6">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>

      {/* Payment Method Section Skeleton */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="space-y-1.5 pl-6">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
    </div>
  );

  /**
   * Renders the billing content
   */
  const renderContent = () => {
    const addressLines = formatAddress();
    const paymentMethod = formatPaymentMethod();

    return (
      <div className="grid gap-6 md:grid-cols-2">
        {/* Address Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span>{t.billing.orgBilling.billingAddress}</span>
          </div>
          <div className="pl-6">
            {addressLines && addressLines.length > 0 ? (
              <div className="space-y-0.5">
                {addressLines.map((line, index) => (
                  <p key={index} className="text-sm">
                    {line}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t.billing.orgBilling.noAddress}</p>
            )}
          </div>
        </div>

        {/* Payment Method Section */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <CreditCard className="h-4 w-4" />
            <span>{t.billing.orgBilling.paymentMethod}</span>
          </div>
          <div className="pl-6">
            {paymentMethod ? (
              <div className="space-y-0.5">
                <p className="text-sm">
                  {paymentMethod.typeName}
                  {paymentMethod.details && ` ${paymentMethod.details}`}
                </p>
                {paymentMethod.expiry && (
                  <p className="text-sm text-muted-foreground">
                    {t.billing.orgBilling.expires} {paymentMethod.expiry}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t.billing.orgBilling.noPaymentMethod}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>{t.billing.orgBilling.title}</CardTitle>
        <CustomerPortalButton variant={"outline"} size="sm" />
      </CardHeader>
      <CardContent>
        {error ? (
          <QueryErrorState
            error={error}
            message={t.billing.orgBilling.failedToLoad}
            onRetry={() => void refetch()}
          />
        ) : isLoading ? (
          renderSkeleton()
        ) : (
          renderContent()
        )}
      </CardContent>
    </Card>
  );
}
